<script>
  import io from 'socket.io-client';
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import { apiServer, socketServer } from '../../helpers/host-sniffer';
  import cookie from '../../helpers/cookie.js';
  import Login from './Login.svelte';
  import Logs from './Logs.svelte';
  import rooms from '../../rooms.js';

  export let room;

  // Validate room (todo: move this to database)
  if (!['public', 'expo-pub', 'family-room'].includes(room)) {
    window.location = '/';
  }

  const thisRoom = rooms[room];

  let key,
    name,
    loginFailed,
    peers = [], // Signed in peers
    visitors = {},
    socketCount = [], // Connected but not necessarilly signed in
    socketsTotal;

  let socket = getSocket();

  async function handleLogin(e) {
    // Dispatched from Login.svelte
    login(e.detail);
  }

  async function login(adminKey) {
    // Try to login with this key.
    const res = await fetch(apiServer + '/admin/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },
      body: JSON.stringify({ key: adminKey }),
    });
    const resText = await res.text();
    if (resText == 'failed') {
      loginFailed = true;
      console.log('Login failed: Invalid key');
      return;
    }
    // Key is valid. Proceed.
    key = adminKey;
    name = resText;
    loginFailed = false;
    cookie.set('key', key);
    // await socket;
    socket.emit('getConnections', key);
    socket.emit('getSocketCount', key);
    let v = await fetch(apiServer + '/visitors');
    visitors = await v.json();
  }

  function handleLogout() {
    cookie.delete('key');
    socket.disconnect();
    socket = null;
    key = null;
    name = null;
  }

  async function getSocket() {
    socket = await io(socketServer, { secure: true });
    socket.on('connect', () => {
      socket.emit('join', room);
      socket.on('connections', async (connections) => {
        if (connections && !Object.keys(connections).length) {
          peers = {};
          return null;
        }
        delete connections[socket.id]; // Remove self.
        const filteredPeers = {};
        for (let id in connections) {
          // Add back peers that are still connected, or create a new peer.
          filteredPeers[id] = peers[id] || connections[id];
          // Overwrite with updated props (if any).
          filteredPeers[id].props = { ...filteredPeers[id].props, ...connections[id].props };
        }
        // This also allows locally set arbitrary properties to remain intact. ex: peer.connected
        peers = filteredPeers;
      });
      socket.on('socketCount', (socketCount) => {
        socketCount = socketCount;
      });
    });
    return socket;
  }

  function kickUser(id) {
    socket.emit('kick', key, id);
  }

  onMount(async () => {
    // If a key is already cookied, log in with it.
    key = cookie.get('key');
    key ? login(key) : (key = null);
  });
</script>

<style type="text/postcss">
  .open-close-btn {
    @apply bg-gray-700 w-20 py-1 mx-0 text-gray-800 font-bold cursor-pointer;
    &:hover {
      @apply text-white;
    }
    &:focus {
      @apply outline-none;
    }
    &.active {
      @apply bg-gray-900 text-gray-300 cursor-default;
    }
  }
</style>

<header class="flex h-12 justify-between items-center px-8 bg-gray-800">
  <!-- Left side logo -->
  <div class="flex">
    {@html rooms['public'].theme.logo}
    <span class="ml-3 mt-2">Admin | {thisRoom.theme.name}</span>
  </div>
  {#if key}
    <div id="right-side" class="flex items-center" transition:fade>
      <div
        id="logout-button"
        class="flex py-1 px-3 -mr-3 cursor-pointer rounded-lg hover:bg-gray-900"
        on:click="{handleLogout}">
        <span class="mr-2 font-bold">{name || ''}</span>
        <svg
          style="margin-top: 2px"
          width="17px"
          height="19px"
          viewBox="0 0 17 19"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink">
          <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <g
              id="Desktop-HD"
              transform="translate(-1403.000000, -16.000000)"
              fill="#FFFFFF"
              fill-rule="nonzero">
              <g id="Group" transform="translate(1403.000000, 16.000000)">
                <g id="Group-3">
                  <rect
                    id="Rectangle"
                    x="7.72727273"
                    y="-1.234568e-13"
                    width="1.54545455"
                    height="9.27272727"></rect>
                  <path
                    d="M8.50013039,18.5454545 C3.813011,18.5454545 0,14.5376953 0,9.61124572
                    C0,6.71978295 1.34532374,3.99331171 3.59891703,2.31818182 L4.45242441,3.58686249
                    C2.58998942,4.97120168 1.47805679,7.22352784 1.47805679,9.61124572
                    C1.47805679,13.6809453 4.62818487,16.9914646 8.49986961,16.9914646
                    C12.3715544,16.9914646 15.5214217,13.6809453 15.5214217,9.61124572
                    C15.5214217,7.22352784 14.4097498,4.97147575 12.5475756,3.58713657
                    L13.401083,2.31845589 C15.6546763,3.99385986 17,6.72005703 17,9.61124572
                    C17.0002608,14.5374212 13.1872498,18.5454545 8.50013039,18.5454545 Z"
                    id="Path"></path>
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
    </div>
  {/if}
</header>

<main class="bg-gray-900">
  {#if key === null}
    <Login loginFailed="{loginFailed}" on:handleLogin="{handleLogin}" />
  {:else if key}
    <div id="peers" class="flex flex-col items-center" style="width:9rem; background:rgba(0,0,0,.3">
      <h1 class="my-4 text-xl font-bold">Now ({Object.keys(peers).length})</h1>
      <div class="flex flex-col items-center overflow-y-auto">
        {#each Object.keys(peers) as id (id)}
          <div class="relative" transition:fade>
            <button
              class="bg-red-900 w-5 rounded-full absolute right-0 mr-6 mt-4 top-0"
              on:click="{kickUser(id)}">
              &#10006;
            </button>
            <img
              class="w-24 h-24 rounded-full border-green-400 border-2 mx-6 my-3"
              src="{peers[id].props && peers[id].props.snapshot}"
              alt="{id}" />
          </div>
        {/each}
      </div>
    </div>
    <div id="logs" class="flex flex-col">
      <div class="flex flex-row">
        {#each Object.entries(visitors) as [label, count]}
          <div class="p-8">
            {label}
            <h3 class="font-bold">{count} total</h3>
          </div>
        {/each}
      </div>
      <Logs socket="{socket}" room="{room}" />
    </div>
  {/if}
</main>
